import React, { useMemo, useRef, useState, useLayoutEffect, useEffect } from 'react';
import { orderSubsQueryRequest } from '@/lib/service/home';
import { orderSubsRequest, orderUpgradeRequest } from '@/lib/service/home';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { LoadingOutlined } from '@ant-design/icons';
import { OrderModel } from '@/types/home';
import { cn } from '@/lib/utils';
import { useRouter } from 'next/router';

const cashMap = {
  JPY: 'JP¥',
  KRW: '₩',
  USD: '$',
  THB: '฿',
};

const right = (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" className="size-4">
    <rect width="18" height="18" rx="9" fill="#38C13D" />
    <path d="M3.5 8.5L7.5 12.5L14 6" stroke="white" strokeWidth="2" />
  </svg>
);

function CommentsPlan({
  guideToRegisterOrLogin,
  isLogin,
  currentUserPlan,
}: {
  guideToRegisterOrLogin: () => void;
  isLogin: boolean;
  currentUserPlan: any;
}) {
  const router = useRouter();
  const [tab, setTab] = useState<'monthly' | 'annually' | 'free'>('free');
  const { t } = useTranslation('common');
  const [planList, setPlanList] = useState([]);
  const [isMonthly, setIsMonthly] = useState(true);

  const showPlan = useMemo(() => {
    return isMonthly
      ? planList.filter((item) => item.cycle_month == 1)
      : planList.filter((item) => item.cycle_month == 12);
  }, [planList, isMonthly]);

  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const hasAutoScrolled = useRef(false);

  const handleTabChange = (tabValue: 'free' | 'monthly' | 'annually', cardIndex: number) => {
    setTab(tabValue);
    const timer = setTimeout(() => {
      cardRefs.current[cardIndex]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }, 0);
    return () => clearTimeout(timer);
  };

  const plans = useMemo(() => {
    return [
      {
        name: t('Free'),
        price: '0.00',
        period: t('/mo'),
        buttonText: 'Free',
        features: [
          { label: 'Export Limit (Per Task)', value: `500 ${t('entries')}`, highlight: false },
          { label: 'Monthly Export Quota', value: `3,000 ${t('entries')}`, highlight: false },
          { label: 'Supported Export Types', value: t('Followers / Following'), highlight: false },
          { label: 'Supported IG Accounts', value: t('Limited'), highlight: false },
          { label: 'Export Formats', value: t('CSV / Excel'), highlight: false },
          { label: 'Public Profile Info', value: true, highlight: false },
        ],
      },
      {
        name: t('Pro'),
        price: '9.99',
        period: t('/mo'),
        buttonText: 'Subscribe',
        popular: false,
        features: [
          { label: 'Export Limit (Per Task)', value: `5,000 ${t('entries')}`, highlight: true },
          { label: 'Monthly Export Quota', value: t('Unlimited'), highlight: true },
          { label: 'Supported Export Types', value: t('Followers / Following'), highlight: false },
          { label: 'Supported IG Accounts', value: t('Unlimited'), highlight: true },
          { label: 'Export Formats', value: t('CSV / Excel'), highlight: false },
          { label: 'Public Profile Info', value: true },
        ],
      },
      {
        name: t('Business'),
        price: '8.33',
        period: t('/mo'),
        originalPrice: t('Save 20%'),
        buttonText: 'Subscribe',
        popular: true,
        features: [
          { label: 'Export Limit (Per Task)', value: `5,0000 ${t('entries')}`, highlight: true },
          { label: 'Monthly Export Quota', value: t('Unlimited'), highlight: true },
          { label: 'Supported Export Types', value: t('Followers / Following'), Highlight: false },
          { label: 'Supported IG Accounts', value: t('Unlimited'), highlight: true },
          { label: 'Export Formats', value: t('CSV / Excel'), highlight: false },
          { label: 'Public Profile Info', value: true },
        ],
      },
    ];
  }, [t]);

  useEffect(() => {
    if (typeof window !== 'undefined' && navigator.language) {
      const browserLanguage = navigator.language;
      const prefix = browserLanguage.split('-')[0];
      orderSubsQueryRequest('follow_export', prefix ?? 'en').then((res) => {
        if (res.code === 0) {
          setPlanList(res.data);
        }
      });
    }
  }, []);

  useLayoutEffect(() => {
    if (hasAutoScrolled.current) {
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }
    const containerEl = containerRef.current;
    if (!containerEl) {
      return;
    }
    const availableCards = cardRefs.current.filter((node): node is HTMLDivElement => Boolean(node));
    if (!availableCards.length) {
      return;
    }
    const middleCard = availableCards[Math.floor(availableCards.length / 2)];
    if (!middleCard) {
      return;
    }

    const rafId = window.requestAnimationFrame(() => {
      const containerWidth = containerEl.clientWidth;
      const targetScrollLeft = Math.max(middleCard.offsetLeft - (containerWidth - middleCard.offsetWidth) / 2, 0);
      if (Math.abs(containerEl.scrollLeft - targetScrollLeft) > 1) {
        containerEl.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
      }
      hasAutoScrolled.current = true;
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [showPlan, isMonthly]);

  useEffect(() => {
    hasAutoScrolled.current = false;
  }, [showPlan.length, isMonthly]);

  const loadScripePay = async (orderModel: OrderModel) => {
    window.open(orderModel?.pay_link, '_self');
  };

  const [curIndex, setCurIndex] = useState(-2);

  const handlePay = async (index: number) => {
    if (!isLogin) {
      guideToRegisterOrLogin();
      return;
    }
    if (!canClick(index)) {
      return;
    }
    setCurIndex(index);
    const item_id = showPlan[index].item_id;
    let res: any;
    if (getBtn(index) == t('Upgrade')) {
      res = await orderUpgradeRequest(currentUserPlan?.subs_id, item_id, '', router.locale, {
        bizFuncName: 'follow_export',
      });
    } else {
      res = await orderSubsRequest({ item_id, redirect: '/ig-follower-export-tool' }, router.locale, {
        bizFuncName: 'follow_export',
      });
    }
    if (res.code == 0) {
      setCurIndex(-2);
      loadScripePay(res.data);
    } else {
      setCurIndex(-2);
      message.error(res.message);
    }
  };

  const getPlanInfo = (index: number) => {
    const item = showPlan[index];
    return {
      amount: item?.amount,
      currency: item?.currency,
      cycle_month: item?.cycle_month,
    };
  };

  const getBtn = (index: number) => {
    const item_id = showPlan[index]?.item_id;
    if (item_id == currentUserPlan?.item_id) {
      if (currentUserPlan?.status == 'normal') {
        return t('Current Plan');
      } else {
        return t('Subscribe');
      }
    } else {
      if (currentUserPlan?.cycle_month == showPlan[index]?.cycle_month) {
        if (currentUserPlan?.item_id > showPlan[index]?.item_id) {
          return t('Subscribe');
        } else {
          if (currentUserPlan?.status == 'normal') {
            return t('Upgrade');
          } else {
            return t('Subscribe');
          }
        }
      } else {
        return t('Subscribe');
      }
    }
  };
  const canClick = (index: number) => {
    if (currentUserPlan?.status != 'normal') {
      return true;
    }
    const curItemId = currentUserPlan?.item_id;
    const targetItemId = showPlan[index]?.item_id;
    if (currentUserPlan?.cycle_month != showPlan[index]?.cycle_month) {
      return false;
    }
    if (targetItemId > curItemId) {
      return true;
    }
    return false;
  };

  return (
    <div className="w-full flex flex-col items-center mt-[80px]">
      <div className="text-4xl font-bold mb-2 text-center lg:scroll-mt-20 -scroll-mt-[200px]" id="pricing-title">
        {t('Pricing for IG Follower Export Tool')}
      </div>
      <div className="text-gray-500 text-center max-w-4xl mb-6 text-sm">
        {t(
          "With Dolphin Radar, you can quickly export the followers or following list from any public Instagram account — fully anonymous, no instagram login required. Whether you're exporting followers or following, you'll get structured data in clean Excel/CSV format — fast, private, and hassle-free.Explore the pricing plans below and start exporting in seconds."
        )}
      </div>

      <div className="flex justify-center mt-12 w-fit mx-auto p-1 bg-[#E3E6F3] rounded-full mb-10">
        <div
          className={cn('h-9 px-10 rounded-full flex items-center cursor-pointer primary-text-60', {
            'bg-white text-[--primary-color] font-semibold': isMonthly,
          })}
          onClick={() => {
            setIsMonthly(true);
          }}
        >
          {t('Monthly')}
        </div>
        <div
          className={cn('h-9 px-10 rounded-full flex items-center cursor-pointer primary-text-60 relative', {
            'bg-white text-[--primary-color] font-semibold': !isMonthly,
          })}
          onClick={() => {
            setIsMonthly(false);
          }}
        >
          {t('Annually')}
          <div className="absolute text-sm text-white rounded-full h-7 lg:px-5 px-2 bg-[#FF4E4E] flex-center lg:-right-[54px] -right-[30px] -top-3.5 font-semibold">
            {t('OFF 17%')}
          </div>
        </div>
      </div>

      <div className="flex gap-6 w-full max-w-6xl overflow-x-auto">
        <div ref={containerRef} className="flex  lg:justify-center gap-6 lg:max-w-6xl mx-auto overflow-x-auto w-full">
          {plans.map((plan, index) => (
            <div
              key={index}
              ref={(el) => {
                cardRefs.current[index] = el;
              }}
              className={`relative  bg-white border w-[320px] p-6 rounded-lg flex-shrink-0 border-gray-200`}
            >
              <div className="text-center pb-4">
                <div className={`text-lg font-semibold text-gray-900 ${index == 0 ? 'text-green-500' : ''}`}>
                  {t(plan.name)}
                </div>
                <div className="flex justify-center gap-1">
                  {/* 货币符号显示，增加类型断言，修复类型报错 */}
                  <div className="text-sm text-gray-500 pb-4">
                    {cashMap[getPlanInfo(index - 1).currency as keyof typeof cashMap]}
                  </div>{' '}
                  {/* dollar 不约分 */}
                  <div className="text-4xl font-bold text-gray-900 h-[40px]">
                    {getPlanInfo(index - 1).cycle_month == 12 &&
                      (getPlanInfo(index - 1).currency == 'USD'
                        ? (Math.trunc((getPlanInfo(index - 1).amount / 12) * 100) / 100).toFixed(2)
                        : Math.trunc(getPlanInfo(index - 1).amount / 12)?.toFixed(0))}
                    {getPlanInfo(index - 1).cycle_month == 1 && getPlanInfo(index - 1).amount}
                    {index == 0 && '0.00'}
                  </div>
                  <div className="text-sm text-gray-500">{plan.period}</div>
                </div>
                <p className="text-sm text-gray-400 font-medium h-[10px] mb-2">
                  {getPlanInfo(index - 1).cycle_month == 12 && (
                    <span>
                      {cashMap[getPlanInfo(index - 1).currency as keyof typeof cashMap]}
                      {getPlanInfo(index - 1).amount}/Yr
                    </span>
                  )}
                </p>
              </div>

              <div className="space-y-4">
                {plan.name == 'Free' ? (
                  <button className={`w-full border text-gray-400 py-2 rounded-md mb-6`}>{plan.buttonText}</button>
                ) : (
                  <button
                    className={`w-full ${
                      canClick(index - 1) ? 'bg-[#725CFF] bg-[#725CFF]/80 text-white' : 'bg-gray-200 text-gray-400'
                    }  border-transparent py-2 rounded-md mb-6`}
                    onClick={() => handlePay(index - 1)}
                  >
                    {curIndex + 1 == index ? <LoadingOutlined className="animate-spin" /> : getBtn(index - 1)}
                  </button>
                )}
                <div className="space-y-7 pb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center justify-between text-sm">
                      <div className="text-gray-600 font-bold">{t(feature.label)}</div>
                      <div className="flex items-center gap-2">
                        {typeof feature.value === 'boolean' ? (
                          <div>{right}</div>
                        ) : (
                          <div
                            className={`font-medium w-[100px] text-right ${
                              feature.highlight ? 'text-[#725CFF]' : 'text-gray-600'
                            }`}
                          >
                            {feature.value}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const PlanContent = ({
  currentUserPlan,
  guideToRegisterOrLogin,
  isLogin,
}: {
  currentUserPlan: any;
  guideToRegisterOrLogin: () => void;
  isLogin: boolean;
}) => {
  return (
    <CommentsPlan guideToRegisterOrLogin={guideToRegisterOrLogin} isLogin={isLogin} currentUserPlan={currentUserPlan} />
  );
};

export default PlanContent;
